<template>
  <div class="sale-page sale-users-page">
    <block-buttons
      title="Продажи сотрудников"
      description="Рабочие смены, продажи и показатели сотрудника"
      :actions="toolbarActions"
    >
      <template #icon>
        <ShoppingBag :size="22" />
      </template>
    </block-buttons>

    <form
      class="sale-page__filters"
      @submit.prevent="loadDataWrapper"
    >
      <label>
        Сотрудник

        <select
          v-model="selectedUser"
          :disabled="loading"
          required
          @change="clearData"
        >
          <option value="">Действия</option>

          <option
            v-for="user in users"
            :key="user.id"
            :value="user.id"
          >
            {{ `${user.name} ${user.surname}` }}
          </option>
        </select>
      </label>

      <label>
        Период

        <select
          v-model="period"
          :disabled="loading"
          @change="selectPeriod"
        >
          <option value="1">За месяц</option>
          <option value="3">За 3 месяца</option>
          <option value="6">За 6 месяцев</option>
          <option value="12">За год</option>
          <option value="custom">Произвольный период</option>
        </select>
      </label>

      <label>
        С
        <input
          v-model="dateFrom"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <label>
        По
        <input
          v-model="dateTo"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <button
        type="submit"
        :disabled="loading || !selectedUser"
      >
        Показать
      </button>
    </form>

    <template v-if="!loading && !error && isLoadedWorkingShifts">
      <dl class="sale-users-page__summary">
        <div>
          <dt>Количество смен</dt>
          <dd>{{ workingShifts.length }}</dd>
        </div>

        <div>
          <dt>Сумма продаж</dt>
          <dd>{{ priceToFormat(totalProfit) }}</dd>
        </div>

        <div>
          <dt>Зарплата (оклад + процент)</dt>
          <dd>{{ priceToFormat(salary) }}</dd>
        </div>
      </dl>

      <section
        class="sale-users-page__chart"
        aria-label="График продаж сотрудника"
      >
        <h2>График продаж</h2>

        <div
          v-if="chartData"
          class="sale-users-page__canvas"
        >
          <canvas
            ref="chart"
            role="img"
            aria-label="chart"
          >
            chart
          </canvas>
        </div>

        <p
          v-else
          class="analytics-page__chart-empty"
        >
          Нет данных для графика за выбранный период.
        </p>
      </section>
    </template>

    <data-table
      :rows="renderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['productListName', 'productCodes', 'typePay', 'price', 'createdAtFormatted']"
      title="Продажи выбранной смены"
      description="Рабочие смены, продажи и показатели сотрудника"
      search-placeholder="Товар, код, способ оплаты, цена или дата создания"
      :empty-text="emptyText"
      @retry="loadDataWrapper"
    >
      <template #header>
        <span class="sale-page__count">Всего продаж: {{ sales.length }}</span>
      </template>

      <template #cell-productListName="{ row }">
        <strong>{{ row.productListName }}</strong>
        <small
          v-if="row.isReturned"
          class="sale-page__returned"
        >
          Возврат
        </small>
      </template>

      <template #cell-price="{ row }">
        <strong>{{ priceToFormat(row.price) }}</strong>
      </template>

      <template #cell-createdAt="{ row }">{{ row.createdAtFormatted }}</template>

      <template #cell-isReturned>Возврат</template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с продажей ${row.id}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ShoppingBag, RefreshCw } from 'lucide-vue'
import moment from 'moment'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'
import { priceToFormat, formatDate } from '@/helper'
import Chart from 'chart.js/auto'
import { chartConfig } from '@/routes/sale/statistic/analytics'

export default {
  name: 'SaleUsersV2',
  components: { ShoppingBag, BlockButtons, DataTable },
  mixins: [permissions],
  data () {
    return {
      sales: [],
      workingShifts: [],
      selectedUser: '',
      loading: false,
      error: '',
      period: '1',
      dateFrom: moment().startOf('month').format('DD.MM.YYYY'),
      dateTo: moment().format('DD.MM.YYYY'),
      chartInstance: null
    }
  },
  computed: {
    ...mapGetters('users', ['getActiveUsers']),
    ...mapGetters('user', { isSeller: 'isSeller', currentUser: 'getData' }),
    ...mapGetters('product', ['getDataObject']),
    ...mapGetters('settings', ['getDefaultValues', 'getTableColumns']),
    users () {
      return this.isSeller
        ? [this.currentUser]
        : this.getActiveUsers || []
    },
    renderData () {
      const paymentTypes = this.getDefaultValues?.['type-pay'] || []

      return this.sales.map(sale => ({
        ...sale,
        createdAtFormatted: formatDate(sale.createdAt),
        productListName: sale.productList.map(id => this.getDataObject[id]?.title).filter(Boolean).join(' '),
        productCodes: (sale.productList || []).map(id => this.getDataObject[id]?.code).filter(Boolean).join(' '),
        typePayText: paymentTypes.find(({ code }) => code === sale.typePay)?.name,
        renderPrice: priceToFormat(sale.price),
        renderPurchasePrice: priceToFormat(sale.purchasePrice)
      }))
    },
    toolbarActions () {
      return  [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadDataWrapper
        }
      ]
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['sale'] || [])
      ]
    },
    emptyText () {
      if (!this.selectedUser) {
        return 'Выберите сотрудника'
      }

      return 'Возвратов за выбранный период нет'
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'sale')
        }
      ].filter(({ visible }) => visible)
    },
    totalProfit () {
      return this.workingShifts.reduce((total, workingShifts) => total += Number(workingShifts.totalProfit), 0)
    },
    salary () {
      // TODO: Тут над овынести на бэк
      // Внутри используется константа из бека
      return this.sales.reduce((total, sale) => {
        if (!sale.isReturned) {
          (total += Number(sale.salary))
        }

        return total
      }, 0) + (this.workingShifts.length * 1000)
    },
    isLoadedWorkingShifts () {
      return this.workingShifts.length > 0
    },
    chartData () {
      const { labels, data } = this.workingShifts.reduce((chartData, workingShift) => {
        chartData.labels.push(formatDate(workingShift.openingAt))
        chartData.data.push(workingShift.totalProfit)

        return chartData
      }, {
        labels: [],
        data: []
      })

      return {
       labels,
       datasets: [{
         backgroundColor: '#2563eb',
         borderColor: '#2563eb',
         data,
         label: 'Продажи'
       }]
      }
    }
  },
  watch: {
    currentUser: {
      immediate: true,
      async handler (value) {
        if (!value?.id) {
          return
        }

        this.selectedUser = value.id

        await this.$nextTick()
        await this.loadDataWrapper()
      }
    },
    workingShifts () {
      this.renderCharts()
    }
  },
  methods: {
    ...mapActions('modal', ['openModal']),
    ...mapActions('sale', ['getByDateAndUserId']),
    ...mapActions('sale', ['remove']),
    ...mapActions('workingShift', { loadShifts: 'loadDataByUserId' }),
    priceToFormat,
    formatDate,
    selectPeriod () {
      if (this.period === 'custom') return
      this.dateFrom = moment().subtract(Number(this.period), 'months').format('DD.MM.YYYY')
      this.dateTo = moment().format('DD.MM.YYYY')
      this.clearData()
      this.loadDataWrapper()
    },
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      const { data, error } = await this.getByDateAndUserId({
        userId: this.selectedUser,
        dateRange: [this.dateFrom, this.dateTo]
      })

      this.loading = false
      this.$bus.emit('stop-loader')

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при загрузке данных!',
          text: 'Произошла ошибка при загрузке возвратов. Повторите попытку позже!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.sales = data
      await this.loadShift()
    },
    async loadShift () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      const { data, error } = await this.loadShifts({
        userId: this.selectedUser,
        dateRange: [this.dateFrom, this.dateTo]
      })

      this.loading = false
      this.$bus.emit('stop-loader')

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при загрузке данных!',
          text: 'Произошла ошибка при загрузке списка рабочих смен. Повторите попытку позже!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.workingShifts = data
    },
    clearData () {
      this.error = ''
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction(action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'sale',
              moduleAction: 'sale',
              type: 'info',
              title: `Информация - ${row.name} ${row.surname}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'remove':
          this.removeSale(row)
      }
    },
    async removeSale ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении продажи!',
          clean: false,
          duration: 3000
        })

        return
      }

      await this.loadDataWrapper()

      this.$notify({
        type: 'success',
        title: 'Продажа успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    },
    async renderCharts () {
      await this.$nextTick()
      this.chartInstance?.destroy?.()

      if (!this.chartData) {
        return
      }

      const ref = this.$refs.chart

      const options = {
        key: 'sellers',
        title: 'Продажи',
        type: 'bar',
        unit: 'money'
      }

      if (ref) {
        this.chartInstance = new Chart(ref, chartConfig(options, this.chartData))
      }
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
